import React, { useCallback, useMemo, useState } from "react";

import InlineGroup from "../../../atoms/inlinegroup/InlineGroup";
import VerticalGroup from "../../../atoms/verticalgroup/VerticalGroup";
import { useResultsContext } from "../context/context";
import ResultsButton from "../results-button/ResultsButton";
import { useKeypress } from "../../../../hooks/useKeypress";
import Clickable from "../../../atoms/clickable/Clickable";
import Icon from "../../../atoms/icon/Icon";
import RoundResultsFontMultiplierModal from "./RoundResultsFontMultiplierModal";

interface Props {
  onNext?: () => void;
}

const RoundResultsContainer: React.FC<Props> = ({ children, onNext }) => {
  const context = useResultsContext();
  const [textSizeModalOpen, setTextSizeModalOpen] = useState(false);
  let maxHeight = "calc(100% - 64px)";
  if (context.hideHeader || context.hidePagination) {
    maxHeight = "100%";
  }

  const onBackClick = useCallback(() => {
    context.goPreviousPage();
  }, [context]);

  const onNextClick = useCallback(() => {
    if (onNext) {
      return onNext();
    }
    if (context.pageIndex < context.pages.length - 1) {
      context.goNextPage();
    }
  }, [context, onNext]);

  const onEscClick = useCallback(() => {
    context.exitPresentationMode();
  }, [context]);

  const handlePresentationModeClick = useCallback(() => {
    context.enterPresentationMode();
  }, [context]);

  const keymap: Record<string, () => void> = useMemo(() => {
    if (context.ignoreKeyPress) {
      return {} as Record<string, () => void>;
    }
    return {
      ArrowDown: onNextClick,
      ArrowUp: onBackClick,
      Escape: onEscClick,
    };
  }, [context.ignoreKeyPress, onNextClick, onBackClick, onEscClick]);

  useKeypress({ keymap });

  return (
    <>
      <VerticalGroup
        wide
        className="pt-5 pb-5 pr-10 pl-10"
        full
        style={{ maxHeight }}
      >
        {children}
      </VerticalGroup>
      {textSizeModalOpen && (
        <RoundResultsFontMultiplierModal
          isOpen={textSizeModalOpen}
          onClose={() => setTextSizeModalOpen(false)}
        />
      )}
      {!context.hidePagination && (
        <InlineGroup className="mt-3 pb-10 pr-10 pl-10" spread block>
          <InlineGroup spaceBetweenElements={4} verticalCenter>
            <ResultsButton shadow outline onClick={onBackClick}>
              Back
            </ResultsButton>
            {context.allowPresentationMode && (
              <Clickable onClick={() => setTextSizeModalOpen(true)}>
                <Icon
                  type="textSize"
                  colour="white"
                  size={6}
                  tip={{ content: "Change text size", id: "tezt-size" }}
                />
              </Clickable>
            )}
            {context.allowPresentationMode && (
              <Clickable
                onClick={() => context.updateCastState({ cast: !context.cast })}
              >
                <Icon
                  type="cast"
                  colour={context.cast ? "secondaryBlue" : "white"}
                  size={6}
                  tip={{ content: "Cast to participants", id: "cast" }}
                />
              </Clickable>
            )}
          </InlineGroup>
          <InlineGroup spaceBetweenElements={4} verticalCenter>
            {context.allowPresentationMode && (
              <ResultsButton
                outline
                shadow
                onClick={handlePresentationModeClick}
              >
                Present
              </ResultsButton>
            )}
            {context.pageIndex <= context.pages.length - 1 && (
              <ResultsButton
                outline
                shadow
                onClick={() => context.updateRenderKey(context.renderKey + 1)}
              >
                Refresh
              </ResultsButton>
            )}
            {context.pageIndex < context.pages.length - 1 && (
              <ResultsButton border shadow onClick={onNextClick}>
                Next
              </ResultsButton>
            )}
          </InlineGroup>
        </InlineGroup>
      )}
    </>
  );
};

export default RoundResultsContainer;
